/**
 * SEO metadata configuration for all pages
 * Centralized location for page titles, descriptions, and keywords
 */

const BASE_URL = "https://www.audiated.com";

export interface PageMetadata {
    title: string;
    description: string;
    keywords: string;
    canonical: string;
}

export const SEO_METADATA: Record<string, PageMetadata> = {
    // Homepage
    HOME: {
        title: "Audiated - Free Interactive Ear Training for Musicians",
        description:
            "Master your musical ear with Audiated. Free interactive ear training exercises for intervals, chords, progressions, and melodies. Perfect for musicians of all levels.",
        keywords:
            "ear training, music theory, interval training, chord recognition, music education, audiation, ear training exercises, music practice",
        canonical: `${BASE_URL}/`,
    },

    // Learn Pages
    LEARN_START: {
        title: "Start Here - Learn Music Theory & Ear Training | Audiated",
        description:
            "Begin your ear training journey with Audiated. Learn the fundamentals of music theory, intervals, chords, and progressions. Free guided lessons for all skill levels.",
        keywords:
            "ear training basics, music theory for beginners, learn intervals, learn chords, music education, audiation training",
        canonical: `${BASE_URL}/Learn`,
    },
    LEARN_INTERVALS: {
        title: "How to Identify Intervals by Ear - Free Interval Training for Beginners | Audiated",
        description:
            "Learn interval identification with step-by-step lessons. Master ascending and descending intervals from minor 2nds to octaves. Discover reference songs, practice techniques, and proven methods to recognize musical intervals by ear. Perfect for beginners.",
        keywords:
            "how to identify intervals by ear, interval ear training, learn intervals, interval identification, ascending descending intervals, interval recognition exercises, music theory intervals, ear training for beginners",
        canonical: `${BASE_URL}/Learn/Intervals`,
    },
    LEARN_CHORDS: {
        title: "How to Identify Chords by Ear - Learn Chord Recognition | Audiated",
        description:
            "Master chord identification with proven ear training methods. Learn to recognize major, minor, diminished, augmented, and seventh chords by their sound. Discover the 'zoom-out' technique for instant chord recognition. Step-by-step lessons for all levels.",
        keywords:
            "identify chords by ear, chord ear training, chord recognition, learn chord identification, major minor chords, seventh chords, diminished augmented chords, chord quality recognition, how to hear chords",
        canonical: `${BASE_URL}/Learn/Chords`,
    },
    LEARN_PROGRESSIONS: {
        title: "How to Recognize Chord Progressions by Ear - Harmonic Ear Training | Audiated",
        description:
            "Learn to hear chord changes and identify progressions by ear. Master roman numeral analysis (I-IV-V-I, ii-V-I), recognize diatonic chord movements, and understand harmonic function. Complete guide to hearing progressions in any key. Essential for songwriters, jazz musicians, and producers.",
        keywords:
            "recognize chord progressions by ear, hear chord changes, chord progression ear training, roman numeral analysis, I-IV-V progression, ii-V-I, harmonic ear training, identify progressions, diatonic chords, how to hear progressions",
        canonical: `${BASE_URL}/Learn/Progressions`,
    },
    LEARN_MELODIES: {
        title: "Melodic Dictation Guide - How to Transcribe Melodies by Ear | Audiated",
        description:
            "Master melodic dictation and learn to transcribe melodies by ear. Understand scale degrees (do-re-mi), recognize melodic patterns in major and minor keys, and develop your aural skills. Complete guide to hearing and writing down melodies. Perfect for music students and composers.",
        keywords:
            "melodic dictation, transcribe melodies by ear, scale degrees, learn melodic dictation, solfege do re mi, melodic ear training, aural skills, melody transcription, hearing melodies, melodic patterns",
        canonical: `${BASE_URL}/Learn/Melodies`,
    },

    // Drill Pages
    DRILL_INTERVALS: {
        title: "Free Interval Ear Training Exercises - Practice Interval Identification Online | Audiated",
        description:
            "Practice interval recognition with unlimited free exercises. Identify ascending and descending intervals from minor 2nds to octaves. Customize difficulty, track your progress, and improve interval identification speed. Perfect daily practice for musicians at any level.",
        keywords:
            "interval ear training exercises, free interval practice, interval identification online, practice intervals by ear, interval recognition drill, ascending descending interval exercises, interval training game, ear training practice",
        canonical: `${BASE_URL}/Drill/Intervals`,
    },
    DRILL_CHORDS: {
        title: "Free Chord Ear Training Exercises - Practice Identifying Chords by Ear | Audiated",
        description:
            "Train your ear with free chord identification exercises. Practice recognizing major, minor, diminished, augmented, and seventh chords by sound. Unlimited exercises with instant feedback. Improve chord recognition speed and accuracy for playing songs by ear.",
        keywords:
            "chord ear training exercises, practice identifying chords by ear, free chord recognition, chord identification practice, major minor chord drill, seventh chord exercises, chord quality recognition, learn chords by ear practice",
        canonical: `${BASE_URL}/Drill/Chords`,
    },
    DRILL_PROGRESSIONS: {
        title: "Chord Progression Ear Training Exercises - Practice Hearing Progressions | Audiated",
        description:
            "Master chord progressions with free ear training exercises. Practice hearing I-IV-V, ii-V-I, and popular song progressions. Train your ear to recognize harmonic movement and chord changes in any key. Essential practice for playing songs by ear and songwriting.",
        keywords:
            "chord progression ear training, practice hearing progressions, chord progression exercises, recognize progressions by ear, I-IV-V practice, ii-V-I exercises, harmonic ear training, hear chord changes practice, progression recognition drill",
        canonical: `${BASE_URL}/Drill/Progressions`,
    },
    DRILL_MELODIES: {
        title: "Melodic Dictation Exercises - Practice Transcribing Melodies by Ear | Audiated",
        description:
            "Practice melodic dictation with unlimited free exercises. Train your ear to identify scale degrees, transcribe melodic patterns, and write down melodies in major and minor keys. Develop essential aural skills for transcription, composition, and playing by ear.",
        keywords:
            "melodic dictation exercises, practice transcribing melodies, melodic ear training practice, scale degree identification, melody transcription practice, aural skills exercises, melodic dictation online, transcribe melodies by ear practice",
        canonical: `${BASE_URL}/Drill/Melodies`,
    },

    // Demo Pages
    DEMO_SOUND: {
        title: "Sound Library Demo - Explore Musical Instruments | Audiated",
        description:
            "Explore Audiated's high-quality sound library. Listen to different instruments including piano, guitar, strings, brass, and more. Test audio quality for ear training.",
        keywords:
            "sound library, musical instruments, instrument sounds, piano sounds, guitar sounds, audio demo, ear training sounds",
        canonical: `${BASE_URL}/Demos/Sound`,
    },

    // Feedback Page
    FEEDBACK: {
        title: "Feedback - Share Your Thoughts | Audiated",
        description:
            "Share your feedback and help us improve Audiated. We value your suggestions, bug reports, and feature requests. Help make ear training better for everyone.",
        keywords: "feedback, user feedback, suggestions, bug report, feature request, contact audiated",
        canonical: `${BASE_URL}/feedback`,
    },
};
